import React, { useMemo } from 'react';
import styles from './IncomeStatement.module.css';
import InfoTooltip from 'components/Dashboard/SpotCard/InfoTooltip/InfoTooltip';
import GroupedCollapsibleRows from '../AGTable/CollapsibleTable/GroupedCollapsibleRows';
import AGCollapsibleTable from 'components/AGTable/CollapsibleTable/AGCollapsibleTable';
import IndentedTR from 'components/AGTable/CollapsibleTable/IndentedTR';
import { convertCentsToDisplayPrice } from 'utils/currency';
import { IncomeStatementReport } from 'types';
import { MinGuaranteeShieldIcon } from '../MinimumGuaranteeBox/MinimumGuaranteeBox';
import { getSingleMonthLabel } from './utils';

type TableProps = {
    report: IncomeStatementReport;
    startDate?: string;
    endDate?: string;
};

const Table = ({ report, startDate, endDate }: TableProps) => {
    // minimum_guarantee_amount is a single month's guarantee; it isn't meaningful
    // compared against a multi-month net income total, so don't apply it then. When no
    // dates are given (e.g. in isolated tests), default to treating it as a single month.
    const isSingleMonth = !startDate || !endDate || !!getSingleMonthLabel(startDate, endDate);
    const {
        income_statement: {
            revenue,
            revenue_monthly: revenueMonthly,
            revenue_bulk_rentals: revenueBulkRentals,
            revenue_transient: revenueTransient,
            revenue_enterprise: revenueEnterprise,
            revenue_ticket: revenueTicket,
            refunds,
            tax_amount: taxAmount,
            client_payment_processing_fee: clientPaymentProcessingFee,
            contribution_amount_exclude_tax: contributionAmountExcludeTax,
            net_income: netIncome,
            net_income_monthly: netIncomeMonthly,
            net_income_bulk_rentals: netIncomeBulkRentals,
            net_income_transient: netIncomeTransient,
            net_income_enterprise: netIncomeEnterprise,
            net_income_ticket: netIncomeTicket,
            net_income_adjustment: netIncomeAdjustment,
            minimum_guarantee_amount: minimumGuaranteeAmount,
        },
    } = report;

    // When a minimum guarantee applies, net_income already includes any top-up needed
    // to reach it; show the guaranteed amount as the final value, with the raw
    // (pre-guarantee) amount struck through when it actually needed correcting.
    const netIncomeContent = useMemo(() => {
        const guaranteeApplies = isSingleMonth && !!minimumGuaranteeAmount;
        const correctedNetIncome = guaranteeApplies ? Math.max(netIncome, minimumGuaranteeAmount) : netIncome;

        return (
            <td>
                <span className={styles.netIncomeContent}>
                    {guaranteeApplies && correctedNetIncome !== netIncome && (
                        <span className={styles.strikethroughValue}>{convertCentsToDisplayPrice(netIncome)}</span>
                    )}
                    {convertCentsToDisplayPrice(correctedNetIncome)}
                    {guaranteeApplies && <MinGuaranteeShieldIcon />}
                </span>
            </td>
        );
    }, [netIncome, minimumGuaranteeAmount, isSingleMonth]);

    return (
        <div className={styles.tableContainer}>
            <AGCollapsibleTable>
                <tbody>
                    {/* Revenue */}
                    <GroupedCollapsibleRows
                        parentRowText="Revenue"
                        parentRowContent={<td>{convertCentsToDisplayPrice(revenue)}</td>}
                    >
                        {!!revenueMonthly && (
                            <IndentedTR>
                                <td>Monthlies</td>
                                <td>{convertCentsToDisplayPrice(revenueMonthly)}</td>
                            </IndentedTR>
                        )}
                        {!!revenueBulkRentals && (
                            <IndentedTR>
                                <td>Tenants</td>
                                <td>{convertCentsToDisplayPrice(revenueBulkRentals)}</td>
                            </IndentedTR>
                        )}
                        <IndentedTR>
                            <td>Transients</td>
                            <td>{convertCentsToDisplayPrice(revenueTransient)}</td>
                        </IndentedTR>
                        {!!revenueTicket && (
                            <IndentedTR>
                                <td>Violation Payments</td>
                                <td>{convertCentsToDisplayPrice(revenueTicket)}</td>
                            </IndentedTR>
                        )}
                        {!!revenueEnterprise && (
                            <IndentedTR>
                                <td>Enterprise</td>
                                <td>{convertCentsToDisplayPrice(revenueEnterprise)}</td>
                            </IndentedTR>
                        )}
                        <IndentedTR>
                            <td>Refunds</td>
                            <td>({convertCentsToDisplayPrice(refunds)})</td>
                        </IndentedTR>
                    </GroupedCollapsibleRows>

                    {/* Taxes */}
                    {!!taxAmount && (
                        <tr>
                            <td>Taxes</td>
                            <td>({convertCentsToDisplayPrice(taxAmount)})</td>
                        </tr>
                    )}

                    {/* Payment Processing Fee */}
                    {!!clientPaymentProcessingFee && (
                        <tr>
                            <td>Payment Processing Fee</td>
                            <td>({convertCentsToDisplayPrice(clientPaymentProcessingFee)})</td>
                        </tr>
                    )}
                    {/* AirGarage Commission */}
                    <tr>
                        <td>
                            AirGarage Commission
                            <InfoTooltip
                                text="AirGarage Commission is taken after taxes and refunds,
 and includes all operating expenses."
                            />
                        </td>
                        <td>({convertCentsToDisplayPrice(contributionAmountExcludeTax)})</td>
                    </tr>

                    {/* Net income */}
                    {netIncomeMonthly ||
                    netIncomeEnterprise ||
                    netIncomeTicket ||
                    netIncomeAdjustment ||
                    netIncomeBulkRentals ? (
                        <GroupedCollapsibleRows parentRowText="Net Income" parentRowContent={netIncomeContent}>
                            {!!netIncomeMonthly && (
                                <IndentedTR>
                                    <td>Monthlies</td>
                                    <td>{convertCentsToDisplayPrice(netIncomeMonthly)}</td>
                                </IndentedTR>
                            )}
                            {!!netIncomeBulkRentals && (
                                <IndentedTR>
                                    <td>Tenants</td>
                                    <td>{convertCentsToDisplayPrice(netIncomeBulkRentals)}</td>
                                </IndentedTR>
                            )}
                            <IndentedTR>
                                <td>Transients</td>
                                <td>{convertCentsToDisplayPrice(netIncomeTransient)}</td>
                            </IndentedTR>
                            {!!netIncomeTicket && (
                                <IndentedTR>
                                    <td>Violation Payments</td>
                                    <td>{convertCentsToDisplayPrice(netIncomeTicket)}</td>
                                </IndentedTR>
                            )}
                            {!!netIncomeEnterprise && (
                                <IndentedTR>
                                    <td>Enterprise</td>
                                    <td>{convertCentsToDisplayPrice(netIncomeEnterprise)}</td>
                                </IndentedTR>
                            )}
                            {!!netIncomeAdjustment && (
                                <IndentedTR>
                                    <td>Adjustments</td>
                                    <td>{convertCentsToDisplayPrice(netIncomeAdjustment)}</td>
                                </IndentedTR>
                            )}
                        </GroupedCollapsibleRows>
                    ) : (
                        <tr>
                            <td>Net Income</td>
                            {netIncomeContent}
                        </tr>
                    )}
                </tbody>
            </AGCollapsibleTable>
        </div>
    );
};

export default Table;
